'use client';

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { v4 as uuidv4 } from 'uuid';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';
import { INLINES, BLOCKS } from '@contentful/rich-text-types';
import makeApiCall from '@packages/lib/server-actions/rest-api';
import { graphQlFetchFunction } from '@packages/lib/server-actions/server-action';
import API_URL from '@packages/lib/server-actions/api-urls';
import { getCommonAPIHeaders, getProjectSpecificData } from '@packages/lib/utlils/commonFunction';
import { logClickstreamEvent } from '@packages/lib/utlils/clickstream';

import styles from './exit-intent-form.module.css';

import {
  trackExitIntentViewed,
  trackExitIntentEmailStarted,
  trackExitIntentClosed,
  trackExitIntentRegistration,
} from './ga-tracking';

interface YearOfEntryOption {
  optionId: string;
  optionValue: string;
}

interface FormContent {
  heading?: string;
  description?: string;
  yearLabel?: string;
  legalText?: string;
  legalTextRich?: Document;
  submitLabel?: string;
  successMessage?: string;
  backgroundImage?: string;
}

interface ExitIntentFormProps {
  assetPathPrefix?: string;
  externalUrl?: string;
  /** Source CTA that triggered the popup (e.g. "visit_website", "book_open_day", "get_prospectus", "request_info"). Tracked in GA click_text. */
  sourceClickText?: string;
  onSubmit?: (data: { email: string; year: string }) => void;
  onClose?: () => void;
  onOpenExternal?: (url: string) => void;
}

const assetPathPrefix = '/static/assets';

const imgCloseIcon = `${assetPathPrefix}/icons/00ff9.svg`;

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Rich text render options: open hyperlinks (e.g. terms & conditions) in a new tab
const legalRichTextOptions = {
  renderNode: {
    [INLINES.HYPERLINK]: (node: any, children: any) => (
      <a
        href={node?.data?.uri}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    [BLOCKS.PARAGRAPH]: (node: any, children: any) => (
      <p style={{ margin: 0, padding: 0, fontSize: '12px', lineHeight: '18px' }}>
        {children}
      </p>
    ),
  },
};

export default function ExitIntentForm({
  sourceClickText = '',
  onSubmit,
  onClose,
}: ExitIntentFormProps) {
  

  const [years, setYears] = useState<YearOfEntryOption[]>([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [yearsLoading, setYearsLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [mounted, setMounted] = useState(false);
  const portalRef = useRef<HTMLDivElement | null>(null);
  const emailStartedRef = useRef(false);
  const viewedRef = useRef(false);
  const [formContent, setFormContent] = useState<FormContent>({
    heading: "Don't leave without joining our student community",
    description: 'Our free tools and guides help thousands of students choose their uni. Subscribe to get involved!',
    yearLabel: 'When do you plan to start uni?',
    legalText: 'By clicking subscribe, you confirm that you are over 13 and agree to the terms and conditions and privacy notice, and to become a member of the Whatuni community*',
    submitLabel: 'Sign me up!',
    successMessage: "Thanks, we'll be in touch soon",
  });

  // Mount portal container on client-side only
  useEffect(() => {
    setMounted(true);
    const portalRoot = document.createElement('div');
    portalRoot.id = 'exit-intent-form-portal';
    document.body.appendChild(portalRoot);
    portalRef.current = portalRoot;

    // Prevent background scroll when modal is open
    document.body.style.overflow = 'hidden';

    // GA: exit_intent_pop_up_viewed (fire once on open)
    if (!viewedRef.current) {
      viewedRef.current = true;
      trackExitIntentViewed(sourceClickText);
    }

    return () => {
      if (portalRef.current && portalRef.current.parentNode) {
        portalRef.current.parentNode.removeChild(portalRef.current);
      }
      // Restore background scroll when modal is closed
      document.body.style.overflow = '';
    };
  }, []);

  // Fetch year of entry options on mount
  useEffect(() => {
    const fetchYearOfEntry = async () => {
      try {
        setYearsLoading(true);
        const yearData: YearOfEntryOption[] = await makeApiCall({
          url: `${process.env.NEXT_PUBLIC_BFF_API_DOMAIN}/${API_URL?.newsletterYearOfEntry}`,
          method: 'GET',
          queryParams: { affiliateId: 220703, actionType: 'YOE' },
          body: null,
          headers: {
            'x-api-key': `${process.env.NEXT_PUBLIC_X_API_KEY}`,
          },
        });
        setYears(yearData || []);
        setSelectedYear(yearData?.[0]?.optionValue || ''); // Default to first year
      } catch (error) {
        console.error('Error fetching year of entry:', error);
      } finally {
        setYearsLoading(false);
      }
    };

    fetchYearOfEntry();
  }, []);

  // Fetch exit-intent form content from Contentful
  useEffect(() => {
    const fetchFormContent = async () => {
      try {
        const query = `{
          pageTemplateDynamicPageCollection(
            limit: 1
            where: { urlSlug: "exit-intent-form", website: { websiteName: "${process.env.PROJECT}" } }
          ) {
            items {
              topZoneComponentsCollection(limit: 1) {
                items {
                  ... on MultipleCardContainer {
                    cardSectionTitle
                    shortDescription
                    mediaCardsCollection(limit: 1) {
                      items {
                        ... on DynamicMediaComponent {
                          title
                          subTitle
                          shortDescription
                          longDescription {
                            json
                          }
                          image {
                            imgUpload {
                              url
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }`;

        const data = await graphQlFetchFunction(query);
        console.log(data, "asdasd")
        const component =
          data?.data?.pageTemplateDynamicPageCollection?.items?.[0]
            ?.topZoneComponentsCollection?.items?.[0];
        const fields = component?.mediaCardsCollection?.items?.[0];

        if (component) {
          setFormContent((prev) => ({
            ...prev,
            heading: component.cardSectionTitle || prev.heading,
            description: component.shortDescription || prev.description,
            yearLabel: fields?.subTitle || prev.yearLabel,
            submitLabel: fields?.title || prev.submitLabel,
            successMessage: fields?.shortDescription || prev.successMessage,
            legalTextRich: fields?.longDescription?.json || prev.legalTextRich,
            backgroundImage: fields?.image?.imgUpload?.url || prev.backgroundImage,
          }));
        }
      } catch (error) {
        console.error('Error fetching form content from Contentful:', error);
        // Fall back to default content
      }
    };

    fetchFormContent();
  }, []);
  console.log(formContent,"formContent")
  // Update email on change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    // GA: exit_intent_email_started (fire once when user starts typing)
    if (value && !emailStartedRef.current) {
      emailStartedRef.current = true;
      trackExitIntentEmailStarted(sourceClickText, selectedYear);
    }

    // Clear error when user starts typing
    if (emailError) {
      setEmailError('');
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate email on submit
    if (!email || !EMAIL_REGEX.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // First, check if user already exists
      const normalizedEmail = email.trim().toLowerCase();
      const affiliateId = String(getProjectSpecificData('affiliateId'));
      
      const checkUserResponse = await makeApiCall({
        url: `${process.env.NEXT_PUBLIC_BFF_API_DOMAIN}${API_URL?.checkUserExistsHeW}`,
        method: 'POST',
        queryParams: {},
        body: {
          email: normalizedEmail,
          affiliateId: affiliateId,
        },
        headers: {
          ...getCommonAPIHeaders(),
          Lp: 'Y',
          'x-correlation-id': uuidv4(),
        },
      });

      // Only proceed with registration when the user can sign up (new user)
      const canProceedToSignUp = checkUserResponse?.action === 'PROCEED_TO_SIGN_UP';

      // Show success immediately regardless of user existence
      setShowSuccess(true);
      setIsSubmitting(false);

      // Call parent onSubmit callback if provided
      if (onSubmit) {
        onSubmit({ email, year: selectedYear });
      }

      // If user can sign up, run registration + tracking in background (non-blocking)
      if (canProceedToSignUp) {
        // GA: registration (track_kpi) on successful submit
        trackExitIntentRegistration(sourceClickText, selectedYear);

        try {
          // Call partial registration first to obtain the referralLeadId
          const registerResponse = await makeApiCall({
            url: `${process.env.NEXT_PUBLIC_BFF_API_DOMAIN}${API_URL?.partialRegistration}`,
            method: 'POST',
            queryParams: {},
            body: {
              userDetails: {
                personalDetails: {
                  email: normalizedEmail,
                  inTakeYear:selectedYear,
                },
                cognitoId: null,
              },
              userSourceType:'WU_EXIT_INTENT',
              skipNameValidation: 'Y',
              marketingEmailFlags: 'Y',
              affiliateId: affiliateId,
            },
            headers: {
              ...getCommonAPIHeaders(),
              Lp: 'Y',
              'x-correlation-id': uuidv4(),
            },
          });

          // Clickstream: log exit-intent form submission event.
          // Use referralLeadId from the register response as userId and
          // include the year of entry (interestedIntakeYear) if selected.
          logClickstreamEvent({
            eventType: "SignedUp",
            pageName: localStorage?.getItem("gaPageName") || "",
            signupMethod: "Exit Intent",
            CTATitle: formContent.submitLabel,
            ...(registerResponse?.referralLeadId
              ? { userId: String(registerResponse.referralLeadId) }
              : {}),
            ...(selectedYear ? { interestedIntakeYear: selectedYear } : {}),
          });
        } catch (backgroundError) {
          // Log background registration error but don't fail user experience
          console.error('Background registration error (non-blocking):', backgroundError);
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setEmailError('An error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // GA: exit_intent_pop_up_closed — only fire when closing BEFORE a successful
    // submit. After success, no close event should be tracked.
    if (!showSuccess) {
      trackExitIntentClosed(sourceClickText, selectedYear, emailStartedRef.current);
    }

    if (onClose) {
      onClose();
    }
  };

  if (!mounted || !portalRef.current) {
    return null;
  }

  const modalContent = (
    <div className={styles.backdrop}>
      {/* Modal */}
      <div className={styles.modal}>
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          disabled={isSubmitting}
          className={styles.closeButton}
        >
          <Image
            alt="close"
            src={imgCloseIcon}
            width={16}
            height={16}
            unoptimized
          />
        </button>

        {/* Left Image Section */}
        <div className={styles.imageSection}>
          <Image
            alt=""
            src={formContent?.backgroundImage || ""}
            fill
            sizes="
              (max-width: 767px) 100vw,
              (max-width: 1023px) 50vw,
              364px
            "
            className={styles.image}
            unoptimized
          />
        </div>

        {/* Right Form Panel */}
        <div className={styles.formPanel}>
          {/* Top Content */}
          <div className={styles.topContent}>
            {/* Heading + Description */}
            <div className={styles.headingSection}>
              <p className={styles.heading}>{formContent.heading}</p>
              <p className={styles.description}>{formContent.description}</p>
            </div>

            {/* Success Message */}
            {showSuccess && (
              <div className={styles.successMessage}>
                <svg
                  className={styles.successIcon}
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.707 5.293a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.414 0z"
                    fill="#106519"
                    stroke="#106519"
                    strokeWidth="0.67"
                  />
                </svg>
                <p className={styles.successText}>{formContent.successMessage}</p>
              </div>
            )}

            {/* Form Fields */}
            {!showSuccess && (
              <div className={styles.formFields}>
                {/* Email Section */}
                <div className={styles.emailSection}>
                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    disabled={isSubmitting}
                    placeholder="Email address"
                    className={styles.emailInput}
                  />
                  {emailError && <p className={styles.emailError}>{emailError}</p>}
                </div>

                {/* Year Selector Section */}
                <div className={styles.yearSection}>
                  <p className={styles.yearLabel}>{formContent.yearLabel}</p>
                  <div className={styles.yearButtonsContainer}>
                    {yearsLoading ? (
                      <>
                        {[1, 2, 3, 4].map((i) => (
                          <div key={`skeleton-${i}`} className={styles.yearButtonSkeleton} />
                        ))}
                      </>
                    ) : (
                      years.map((year) => {
                        const active = year.optionValue === selectedYear;
                        return (
                          <button
                            key={year.optionId}
                            type="button"
                            onClick={() => setSelectedYear(year.optionValue)}
                            disabled={isSubmitting}
                            className={`${styles.yearButton} ${active ? styles.active : ''}`}
                          >
                            {year.optionId}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Content */}
          {!showSuccess && (
            <div className={styles.bottomContent}>
              {/* Legal Text */}
              <div className={styles.legalText}>
                {formContent.legalTextRich
                  ? documentToReactComponents(
                      formContent.legalTextRich,
                      legalRichTextOptions
                    )
                  : formContent.legalText}
              </div>

              {/* Submit Button */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={styles.submitButton}
              >
                {isSubmitting ? "Submitting..." : formContent.submitLabel}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, portalRef.current);
}


